import { useEffect, useState } from 'react';
import {
  getProxyAssetUrl,
  resolveAssetUrl,
} from './url';

const isDirectBrowserUrl = (value: string) =>
  value.startsWith('data:') || value.startsWith('blob:');

const isAbsoluteHttpUrl = (value: string) =>
  value.startsWith('http://') || value.startsWith('https://');

/** Prefer storage key for proxy fallback when a full CDN URL was passed in. */
const toProxyKey = (imageKey: string, assetUrl: string): string | null => {
  if (!imageKey || isDirectBrowserUrl(imageKey)) return null;

  if (!isAbsoluteHttpUrl(imageKey)) {
    return imageKey.replace(/^\//, '');
  }

  const base = (assetUrl || '').replace(/\/$/, '');
  if (base && imageKey.startsWith(`${base}/`)) {
    return imageKey.slice(base.length + 1);
  }

  try {
    const pathname = new URL(imageKey).pathname.replace(/^\//, '');
    return pathname || null;
  } catch {
    return null;
  }
};

export const useAssetImageSrc = (imageKey: string, assetUrl: string) => {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!imageKey) {
      setSrc('');
      setFailed(false);
      return;
    }

    setFailed(false);
    setSrc(resolveAssetUrl(imageKey, assetUrl));
  }, [imageKey, assetUrl]);

  const handleError = () => {
    if (!imageKey || isDirectBrowserUrl(imageKey)) {
      setFailed(true);
      return;
    }

    const proxyKey = toProxyKey(imageKey, assetUrl);
    if (!proxyKey) {
      setFailed(true);
      return;
    }

    const proxyUrl = getProxyAssetUrl(proxyKey);
    if (src !== proxyUrl) {
      setSrc(proxyUrl);
      return;
    }

    setFailed(true);
  };

  return { src, failed, handleError };
};
